import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LayoutGrid, Eye, EyeOff, Building2, Users, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  // Fetch invitation info if invite token is present
  useEffect(() => {
    if (inviteToken) {
      fetchInviteInfo();
    }
  }, [inviteToken]);

  const fetchInviteInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/by-token/${inviteToken}`);
      const data = await response.json();

      if (response.ok) {
        setInviteInfo(data);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      // If there's an invite token, accept the invitation
      if (inviteToken && result?.token) {
        try {
          const acceptResponse = await fetch(`${BACKEND_URL}/api/invitations/accept`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${result.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: inviteToken })
          });

          const acceptData = await acceptResponse.json();

          if (acceptResponse.ok) {
            toast.success(`Welcome back! You've joined ${acceptData.target_name}`);
          }
        } catch (err) {
          // Silently fail - user is logged in
          console.error('Failed to accept invitation:', err);
        }
      }

      // Check if onboarding is complete
      const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
      
      if (!onboardingComplete) {
        // Redirect to onboarding first
        if (inviteToken) {
          navigate(`/onboarding?invite=${inviteToken}`);
        } else {
          navigate('/onboarding');
        }
      } else {
        // Check if user is admin and redirect accordingly
        const isAdmin = Boolean(
          result?.user?.is_admin === true ||
          (result?.user?.role != null && String(result.user.role).trim().toLowerCase() === 'admin')
        );
        if (isAdmin) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('timeout') || err.message?.includes('Network Error'));
      setError(
        isNetworkError
          ? `Cannot reach backend at ${BACKEND_URL}. Is the server running? (Start it from the backend folder: uvicorn server:app --reload --port 8000)`
          : (err.response?.data?.detail || 'Invalid email or password')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#2563EB] flex items-center justify-center">
            <LayoutGrid className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-[#1E293B]">BoardChat</span>
        </div>

        {/* Invitation Info Banner */}
        {inviteInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                inviteInfo.target_type === 'hub' ? 'bg-purple-500' : 'bg-blue-500'
              }`}>
                {inviteInfo.target_type === 'hub' ? (
                  <Users className="w-5 h-5 text-white" />
                ) : (
                  <Building2 className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{inviteInfo.inviter_name}</span> invited you to join
                </p>
                <p className="font-semibold text-blue-900">{inviteInfo.target_name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-[#1E293B] text-center mb-2">
            {inviteInfo ? 'Sign in to join' : 'Welcome back'}
          </h1>
          <p className="text-[#64748B] text-center mb-6">
            {inviteInfo ? 'Log in to accept the invitation' : 'Sign in to your account'}
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#1E293B]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#1E293B]">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white py-3"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : (
                inviteInfo ? (
                  <>
                    <LogIn size={18} className="mr-2" />
                    Sign in & Join
                  </>
                ) : 'Sign in'
              )}
            </Button>
          </form>

          <p className="text-center text-[#64748B] text-sm mt-6">
            Don't have an account?{' '}
            <Link 
              to={inviteToken ? `/register?invite=${inviteToken}` : '/register'} 
              className="text-[#2563EB] hover:underline font-medium"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
